class DevProductInformation extends HTMLElement {
  connectedCallback() {
    // Each part is isolated so one failure (e.g. Swiper) can't stop the purchase logic.
    [
      'deferGallerySlides',
      'initSwiper',
      'bindGalleryNav',
      'bindQuantity',
      'bindPurchaseType',
      'bindAddToCart',
      'bindBuyNow',
      'bindAccordions',
      'bindStickyCTA',
      'applyOfferPrice',
    ].forEach((name) => {
      try {
        this[name]();
      } catch (err) {
        console.error(`dev-product-information: ${name} failed`, err);
      }
    });

    // Back/forward cache restores the old DOM state — re-apply the visible tab so the plan matches it
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.syncPurchaseType();
    });
  }

  /* =============================================
     Discount-link offer — reveal the pre-rendered one-time offer prices
     ============================================= */

  /**
   * Shows the offer prices only to visitors whose session carries the offer code,
   * i.e. who arrived via /discount/<code>. Checkout is untouched: the real Shopify
   * discount does the charging, this only changes what the page displays.
   */
  async applyOfferPrice() {
    const offerCode = this.dataset.offerCode?.trim().toLowerCase();
    if (!offerCode) return;

    // "Ends on" (YYYY-MM-DD): the code stays in the visitor's cookie after the discount
    // expires, so stop showing the offer once that day is over.
    const ends = this.dataset.offerEnds?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ends && new Date() >= new Date(+ends[1], +ends[2] - 1, +ends[3] + 1)) return;

    const codes = await this.getDiscountCodes();
    if (!codes.includes(offerCode)) return;

    this.querySelectorAll('[data-offer-price]').forEach((option) => {
      option.dataset.price = option.dataset.offerPrice;
      option.dataset.save = option.dataset.offerSave;
      option.dataset.points = option.dataset.offerPoints;
    });
    this.querySelectorAll('[data-offer]').forEach((el) => {
      el.hidden = el.dataset.offer !== 'alt';
    });

    // Refresh Add to Cart + sticky price if One-Time is already the active tab
    const onetimePanel = this.querySelector('[data-sub-panel="one-time"]');
    if (onetimePanel && !onetimePanel.hidden) this._activateOnetime?.();
  }

  /**
   * /discount/<code> leaves the code in the `discount_code` cookie and on the cart,
   * where it is listed even while the cart is empty (as not yet applicable).
   * @returns {Promise<string[]>} lowercased codes
   */
  async getDiscountCodes() {
    const codes = [];
    const cookie = document.cookie.split('; ').find((c) => c.startsWith('discount_code='));
    if (cookie) codes.push(decodeURIComponent(cookie.slice('discount_code='.length)));

    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      (cart.discount_codes || []).forEach((d) => codes.push(d.code));
    } catch { /* cookie alone is enough */ }

    return codes.map((c) => c.trim().toLowerCase());
  }

  /* =============================================
     Swiper Gallery
     ============================================= */

  /**
   * Gallery slides after the first are rendered with data-src/data-srcset so they
   * don't compete with the first image (and the tracking pixels) on slow mobile
   * connections. Load them once the page has finished loading, or right away if
   * the shopper reaches for the gallery first.
   */
  deferGallerySlides() {
    const gallery = this.querySelector('.dev-product-information__gallery');
    if (!gallery || !gallery.querySelector('img[data-src]')) return;

    const load = () => {
      gallery.querySelectorAll('img[data-src]').forEach((img) => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
      });
      ['pointerdown', 'touchstart', 'focusin'].forEach((type) => gallery.removeEventListener(type, load));
    };
    this.loadGallerySlides = load;

    ['pointerdown', 'touchstart', 'focusin'].forEach((type) =>
      gallery.addEventListener(type, load, { once: true, passive: true })
    );

    const afterLoad = () => setTimeout(load, 1500);
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  }

  initSwiper() {
    const mainEl = this.querySelector('[data-main-swiper]');
    const thumbsEl = this.querySelector('[data-thumbs-swiper]');
    const prevBtn = this.querySelector('[data-nav="prev"]');
    const nextBtn = this.querySelector('[data-nav="next"]');

    if (!mainEl) return;

    // Wait for Swiper to be available
    if (typeof Swiper === 'undefined') {
      const script = document.querySelector('script[src*="swiper-bundle"]');
      if (script) {
        script.addEventListener('load', () => this.initSwiper());
      }
      return;
    }

    // Thumbnails swiper
    let thumbsSwiper = null;
    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        direction: 'vertical',
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        breakpoints: {
          0: {
            direction: 'horizontal',
            spaceBetween: 7,
          },
          769: {
            direction: 'vertical',
            spaceBetween: 10,
          },
        },
      });
    }

    // Main swiper
    const mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
      on: {
        init: (swiper) => this.updateArrowStates(swiper, prevBtn, nextBtn),
        slideChange: (swiper) => {
          this.loadGallerySlides?.();
          this.updateArrowStates(swiper, prevBtn, nextBtn);
        },
      },
    });

    this.mainSwiper = mainSwiper;
  }

  updateArrowStates(swiper, prevBtn, nextBtn) {
    if (!prevBtn || !nextBtn) return;

    const isBeginning = swiper.isBeginning;
    const isEnd = swiper.isEnd;

    prevBtn.classList.toggle('dev-product-information__nav--disabled', isBeginning);
    prevBtn.disabled = isBeginning;

    nextBtn.classList.toggle('dev-product-information__nav--disabled', isEnd);
    nextBtn.disabled = isEnd;
  }

  /**
   * The prev/next arrows overlap the photo and cover the ingredient amounts on
   * the ingredients slide, so on hover-capable desktops they are revealed only
   * while the gallery is hovered or keyboard-focused. Touch devices and mobile
   * get no arrows at all — the gallery is swiped there.
   *
   * This only picks the mode; the reveal itself lives in CSS (`:hover` /
   * `:focus-within`). Pointer listeners are deliberately avoided: Swiper
   * disables the prev/next button on the first/last slide, and disabling a
   * focused button fires `focusout`, which would hide the arrows while the
   * cursor was still on the image.
   */
  bindGalleryNav() {
    const wrapper = this.querySelector('.dev-product-information__main-image');
    const prevBtn = this.querySelector('[data-nav="prev"]');
    const nextBtn = this.querySelector('[data-nav="next"]');

    // Single-image products render no arrows at all.
    if (!wrapper || !prevBtn || !nextBtn) return;

    const query = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)');

    const applyMode = () => {
      const canHover = query.matches;
      wrapper.classList.toggle('dev-product-information__main-image--hover-nav', canHover);
      wrapper.classList.toggle('dev-product-information__main-image--nav-hidden', !canHover);
    };

    query.addEventListener('change', applyMode);
    applyMode();
  }

  /* =============================================
     Quantity
     ============================================= */

  bindQuantity() {
    const input = this.querySelector('[data-qty-input]');
    if (!input) return;

    this.querySelector('[data-qty="minus"]')?.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      if (val > 1) input.value = val - 1;
    });

    this.querySelector('[data-qty="plus"]')?.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      input.value = val + 1;
    });
  }

  /* =============================================
     Subscription Block — tab toggle + option selection
     ============================================= */

  bindPurchaseType() {
    const wrapper = this.querySelector('[data-sub-wrapper]');
    if (!wrapper) return;

    const sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    const subscribePanel = wrapper.querySelector('[data-sub-panel="subscribe"]');
    const onetimePanel = wrapper.querySelector('[data-sub-panel="one-time"]');

    // Add to Cart price + savings reflect the selected purchase option
    const atcAmount = this.querySelector('[data-atc-amount]');
    const atcSave = this.querySelector('[data-atc-save]');
    const qtyInput = this.querySelector('[data-qty-input]');
    const rewardPoints = this.querySelector('[data-reward-points]');
    const setAtcPrice = (option) => {
      if (!option) return;
      if (atcAmount && option.dataset.price) atcAmount.textContent = option.dataset.price;
      if (atcSave) atcSave.textContent = option.dataset.save ? ` (Save ${option.dataset.save})` : '';
      // Quantity follows the selected pack (e.g. 2-Pack adds 2 jars with the plan)
      if (qtyInput && option.dataset.packQty) qtyInput.value = option.dataset.packQty;
      // Paw Points follow the selected pack as well
      if (rewardPoints && option.dataset.points) {
        rewardPoints.textContent = `${option.dataset.points} Paw Points`;
      }
      // Mirror the selected price into the floating sticky island
      const stickyPrice = this.querySelector('[data-sticky-price]');
      if (stickyPrice && option.dataset.price) {
        stickyPrice.textContent = (stickyPrice.dataset.pricePrefix || '') + option.dataset.price;
      }
    };

    const setTabActive = (tab) => {
      wrapper.querySelectorAll('[data-sub-tab]').forEach((t) => {
        const active = t.dataset.subTab === tab;
        t.classList.toggle('dev-product-information__sub-tab--active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (subscribePanel) subscribePanel.hidden = tab !== 'subscribe';
      if (onetimePanel) onetimePanel.hidden = tab !== 'one-time';
      // Each tab has its own perks list
      wrapper.querySelectorAll('[data-sub-perks]').forEach((list) => {
        list.hidden = list.dataset.subPerks !== tab;
      });
    };

    const checkedOption = (panel) =>
      panel?.querySelector('[data-purchase-radio]:checked')?.closest('[data-purchase-option]') ||
      panel?.querySelector('[data-purchase-option]');

    // Subscribe: one recurring plan, quantity 1
    const activateSubscribe = () => {
      this._purchaseType = 'subscribe';
      setTabActive('subscribe');
      const option = checkedOption(subscribePanel);
      const planId = option?.querySelector('[data-purchase-radio]')?.value;
      if (sellingPlanInput) sellingPlanInput.value = planId || '';
      setAtcPrice(option);
    };

    // One-time: no plan; quantity follows the selected pack (2-Pack = 2 jars)
    const activateOnetime = () => {
      this._purchaseType = 'one-time';
      setTabActive('one-time');
      if (sellingPlanInput) sellingPlanInput.value = '';
      setAtcPrice(checkedOption(onetimePanel));
    };

    // Tab button clicks
    wrapper.querySelectorAll('[data-sub-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.dataset.subTab === 'subscribe') activateSubscribe();
        else activateOnetime();
      });
    });

    // Option card selection (either panel)
    [subscribePanel, onetimePanel].forEach((panel) => {
      panel?.addEventListener('change', (e) => {
        const radio = e.target.closest('[data-purchase-radio]');
        if (!radio) return;
        panel.querySelectorAll('[data-purchase-option]').forEach((o) => {
          o.classList.toggle('dev-product-information__sub-option--selected', o.contains(radio));
        });
        if (panel === subscribePanel) activateSubscribe();
        else activateOnetime();
      });
    });

    // Expose for sticky CTA sync
    this._activateSubscribe = activateSubscribe;
    this._activateOnetime = activateOnetime;

    // Initialize from the tab already shown — the inline script may have switched it before this ran.
    // Subscribe & Save is the server-rendered default.
    this.syncPurchaseType();
    this.setAttribute('data-purchase-ready', '');
  }

  /** Re-applies whichever tab is visibly active (aria-pressed), so the plan input always matches it. */
  syncPurchaseType() {
    if (!this._activateSubscribe) return;
    if (this.getActiveTab() === 'one-time') this._activateOnetime();
    else this._activateSubscribe();
  }

  /** @returns {'subscribe' | 'one-time' | null} the active tab; null when the product has no subscription widget */
  getActiveTab() {
    const wrapper = this.querySelector('[data-sub-wrapper]');
    if (!wrapper) return null;
    const pressed = wrapper.querySelector('[data-sub-tab][aria-pressed="true"]');
    return pressed?.dataset.subTab || this._purchaseType || 'subscribe';
  }

  /**
   * The selling plan to send, derived at submit time from the visible tab — never from a stale input.
   * Clears the hidden input whenever One-Time is active.
   * @returns {number | null}
   */
  getSellingPlanId() {
    const input = this.querySelector('[data-selling-plan-input]');
    if (this.getActiveTab() !== 'subscribe') {
      if (input) input.value = '';
      return null;
    }
    const planId =
      this.querySelector('[data-sub-panel="subscribe"] [data-purchase-radio]:checked')?.value ||
      input?.dataset.defaultPlan;
    if (input) input.value = planId || '';
    return planId ? parseInt(planId, 10) : null;
  }

  /**
   * "Last choice wins": a variant can't sit in the cart as both one-time and subscription.
   * Removes lines of the same variant with the opposite purchase type and returns their quantity,
   * to be added under the newly chosen type. Same-type lines are left to Shopify, which merges them.
   * The removed lines are kept on this._takenLines so a failed add can put them back.
   * @param {number} variantId
   * @param {number | null} sellingPlanId
   * @returns {Promise<number>} quantity moved over
   */
  async takeOppositeLines(variantId, sellingPlanId) {
    let moved = 0;
    this._takenLines = [];
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return 0;
      const cart = await res.json();
      const opposite = (cart.items || []).filter(
        (line) => line.variant_id === variantId && !!line.selling_plan_allocation !== !!sellingPlanId
      );
      for (const line of opposite) {
        const change = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
        if (change.ok) {
          moved += line.quantity;
          this._takenLines.push(line);
        }
      }
    } catch { /* cart unreadable — just add */ }
    return moved;
  }

  /** Puts back the lines takeOppositeLines removed, when the add that should replace them failed. */
  async restoreTakenLines() {
    const items = (this._takenLines || []).map((line) => {
      const item = { id: line.variant_id, quantity: line.quantity };
      const planId = line.selling_plan_allocation?.selling_plan?.id;
      if (planId) item.selling_plan = planId;
      if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
      return item;
    });
    this._takenLines = [];
    if (!items.length) return;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
    } catch { /* best effort */ }
  }

  /* =============================================
     Add to Cart (AJAX + open drawer)
     ============================================= */

  bindAddToCart() {
    const form = this.querySelector('#dev-product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const variantId = this.querySelector('[data-variant-id]')?.value;
      const quantity = this.querySelector('[data-qty-input]')?.value || 1;
      const addBtn = form.querySelector('[name="add"]');

      if (!variantId) return;

      // Disable button + show loading state
      if (addBtn) {
        addBtn.classList.add('dev-btn--disabled');
        addBtn.querySelector('.dev-btn__text').textContent = 'Adding...';
      }

      try {
        // selling_plan only while Subscribe & Save is the active tab
        const id = parseInt(variantId, 10);
        const sellingPlanId = this.getSellingPlanId();
        const moved = await this.takeOppositeLines(id, sellingPlanId);
        const item = { id, quantity: parseInt(quantity, 10) + moved };
        if (sellingPlanId) item.selling_plan = sellingPlanId;
        const body = { items: [item] };

        const response = await fetch(
          window.Theme?.routes?.cart_add_url || '/cart/add.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          }
        );

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || 'Could not add to cart');
        }

        this._takenLines = []; // added — nothing to put back from here on
        await response.json();

        // Open cart drawer
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer) {
          // Refresh drawer content first, then open
          const sectionId = drawer.querySelector('cart-items-component')?.dataset?.sectionId;
          if (sectionId) {
            const sectionsRes = await fetch(`${window.location.pathname}?sections=${sectionId}`);
            const sections = await sectionsRes.json();
            const html = sections[sectionId];
            if (html) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newInner = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
              const currentInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
              if (newInner && currentInner) {
                currentInner.innerHTML = newInner.innerHTML;
              }
            }
          }

          drawer.open();
        }

        // Fetch fresh cart count and update header badge
        try {
          const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          const cart = await cartRes.json();
          document.querySelectorAll('.site-header__cart-count').forEach((el) => {
            el.textContent = cart.item_count;
            el.setAttribute('aria-label', `${cart.item_count} items`);
            el.style.display = cart.item_count > 0 ? '' : 'none';
          });
          // Also update any hidden count spans
          document.querySelectorAll('.cart-bubble__text').forEach((el) => {
            el.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
          });
          document.querySelectorAll('.cart-bubble').forEach((el) => {
            el.classList.toggle('hidden', cart.item_count === 0);
          });
        } catch { /* ignore badge update failure */ }

      } catch (err) {
        await this.restoreTakenLines();
        console.error('Add to cart failed:', err);
        alert(err.message || 'Could not add item to cart. Please try again.');
      } finally {
        if (addBtn) {
          addBtn.classList.remove('dev-btn--disabled');
          addBtn.querySelector('.dev-btn__text').textContent = 'Add to Cart';
        }
      }
    });
  }

  /* =============================================
     Buy Now
     ============================================= */

  bindBuyNow() {
    const btn = this.querySelector('[data-buy-now]');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const variantId = this.querySelector('[data-variant-id]')?.value;
      const quantity = this.querySelector('[data-qty-input]')?.value || 1;

      if (!variantId) return;

      btn.disabled = true;

      try {
        const id = parseInt(variantId, 10);
        const sellingPlanId = this.getSellingPlanId();
        const moved = await this.takeOppositeLines(id, sellingPlanId);
        const item = { id, quantity: parseInt(quantity, 10) + moved };
        if (sellingPlanId) item.selling_plan = sellingPlanId;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });

        if (response.ok) {
          window.location.href = '/checkout';
        } else {
          await this.restoreTakenLines();
          btn.disabled = false;
        }
      } catch {
        await this.restoreTakenLines();
        btn.disabled = false;
      }
    });
  }

  /* =============================================
     Accordions — animate open/close
     ============================================= */

  bindAccordions() {
    this.querySelectorAll('.dev-product-information__accordion').forEach((details) => {
      const summary = details.querySelector('summary');
      const body = details.querySelector('.dev-product-information__accordion-body');
      if (!summary || !body) return;

      // Set initial height if already open
      if (details.open) {
        body.style.height = body.scrollHeight + 'px';
      }

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        if (details.open) {
          // Closing: set explicit height first, then collapse to 0
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(() => {
            body.style.height = '0px';
          });
          body.addEventListener(
            'transitionend',
            () => {
              details.open = false;
            },
            { once: true },
          );
        } else {
          // Opening: open details, measure, animate from 0
          details.open = true;
          const targetHeight = body.scrollHeight;
          body.style.height = '0px';
          requestAnimationFrame(() => {
            body.style.height = targetHeight + 'px';
          });
        }
      });
    });
  }
  /* =============================================
     Sticky CTA — floating island shown once the buy box scrolls out of view
     ============================================= */

  bindStickyCTA() {
    const bar = this.querySelector('[data-sticky-cta]');
    if (!bar) return;

    // ---- Visibility: show only once the buy box is scrolled above the viewport ----
    let formAboveViewport = false;
    let footerVisible = false;

    const setVisible = () => {
      const visible = formAboveViewport && !footerVisible;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const form = this.querySelector('#dev-product-form');
    if (form) {
      new IntersectionObserver(
        ([entry]) => {
          formAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          setVisible();
        },
        { threshold: 0 }
      ).observe(form);
    }

    // Hide the island once the footer scrolls into view
    const footer = document.querySelector('footer, .site-footer, footer-component');
    if (footer) {
      new IntersectionObserver(
        ([entry]) => { footerVisible = entry.isIntersecting; setVisible(); },
        { threshold: 0 }
      ).observe(footer);
    }

    // ---- Shop Now: reuse the main form submit (adds current selection + opens drawer) ----
    bar.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
      const mainBtn = this.querySelector('[name="add"]');
      if (mainBtn?.classList.contains('dev-btn--disabled')) return;
      this.querySelector('#dev-product-form')?.requestSubmit();
    });
  }
}

customElements.define('dev-product-information', DevProductInformation);
